import { SkuCardProps } from '@/types/props/components/skus';
import { Card, Col, Row } from 'react-bootstrap';
import { faHeart as solidFaHeart } from '@fortawesome/free-solid-svg-icons';
import { faHeart } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LoadingComponent from '../../../LoadingComponent';
import SkuDescription from '../SkuCardDescription';
import SkuCardImage from './SkuCardImageDesktop';
import SkuCardLowestAsk from './SkuCardLowestAskDesktop';
import translationFallback from '@/utilites/translations/translationFallback';
import SkuCardTrendingPercent from '../SkuCardTrendingPercent';
import clsx from 'clsx';
import event from '@/utilites/gTag';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import useWishlist from '@/hooks/useWishlist';
import { SkuDTO } from '@/types/api/sku';
import useBackendUser from '@/hooks/useBackendUser';
import useTrendingPercent from '@/hooks/useTrendingPercent';
import * as Tooltip from '@radix-ui/react-tooltip';
import { useEffect, useState } from 'react';
import useEvents from '@/hooks/useEvents';
import { usePathname } from 'next/navigation';
import { useAnalytics } from '@/hooks/useAnalytics';
export default function SkuCardDesktop({ sku }: SkuCardProps) {
  const [releaseDate, setReleaseDate] = useState<string | null>(null);
  const { data: user } = useBackendUser();
  const { isInWishlist, toggleFromWishlist } = useWishlist();
  const { t: tCommon } = useTranslation('common');
  const { getPageType } = useAnalytics();
  const { fireGlobalToBottleButtonClickEvent } = useEvents('', []);

  const handleSkuCardClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.preventDefault();
    if (sku) {
      fireGlobalToBottleButtonClickEvent({
        slug: sku.slug,
        product_name: sku.name || '',
        product_category: sku?.category?.name || '',
        product_price: sku.lowestAsk || 0,
        page_type: getPageType(),
        price_ask: sku.lowestAsk || 0,
        brand_name_page: sku?.brand?.name || '',
      });
      window.location.href = `/${sku.slug}`;
    }
  };
  const locale = useRouter().locale;
  const trendingPercent = useTrendingPercent(sku?.orders || [], sku?.retailPrice || 0);

  useEffect(() => {
    if (sku) {
      const currentDate = new Date();
      const skuReleaseDate = sku.releaseDate ? new Date(sku.releaseDate) : null;
      setReleaseDate(
        skuReleaseDate && skuReleaseDate.getTime() > currentDate.getTime() ? sku.releaseDate : null
      );
    }
  }, [sku]);

  return (
    <div className="tw-relative tw-h-full">
      {releaseDate && (
        <Tooltip.Provider>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <div className="tw-absolute tw-left-2 tw-top-2 tw-z-20 tw-cursor-pointer tw-rounded-lg tw-bg-custom-gray tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-text">
                <span>{new Date(releaseDate).toLocaleString(locale, { month: 'short' })}</span>
                <span className="tw-mx-2 tw-text-text/20">|</span>
                <span>{new Date(releaseDate).getDate()}</span>
              </div>
            </Tooltip.Trigger>
            <Tooltip.Content
              className="tw-rounded-md tw-bg-black tw-px-2 tw-py-1 tw-text-sm tw-text-white tw-shadow-lg"
              side="top"
              sideOffset={5}
            >
              {new Date(releaseDate).toLocaleDateString(locale, {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
              <Tooltip.Arrow className="tw-fill-black" />
            </Tooltip.Content>
          </Tooltip.Root>
        </Tooltip.Provider>
      )}
      <LoadingComponent
        as={'button'}
        loading={sku === null}
        componentProps={{
          className:
            'tw-bg-transparent tw-border-none tw-appearance-none position-absolute top-0 end-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-w-[23%] tw-h-auto tw-aspect-square',
          'aria-label':
            sku === null
              ? 'loading'
              : isInWishlist(sku?.slug ?? '')
                ? 'Remove from wishlist'
                : 'Add to wishlist',
          onClick: e => {
            e.stopPropagation();
            toggleFromWishlist(sku as SkuDTO);
          },
        }}
      >
        <FontAwesomeIcon
          icon={isInWishlist(sku?.slug ?? '') ? solidFaHeart : faHeart}
          className={`font-size-24 ${
            isInWishlist(sku?.slug ?? '') ? 'text-danger' : 'text-muted'
          } `}
        />
      </LoadingComponent>
      <Card
        as="a"
        aria-label={sku ? `Go to bottle ${sku.name}` : 'Link disabled'}
        className={clsx(
          { 'tw-pointer-events-none tw-overflow-hidden': !sku },
          'tw-font-inherit s-card-shadow h-100 tw-ease tw-cursor-pointer tw-border tw-border-none tw-border-platinum-gray tw-bg-none tw-p-0 tw-text-inherit tw-no-underline tw-outline-inherit tw-transition-all tw-duration-500 hover:tw-shadow-fancyBoxShadow hover:tw-transition-all'
        )}
        tabIndex={sku ? 0 : -1}
        onClick={handleSkuCardClick}
        href={`/${sku?.slug}`}
      >
        <SkuCardImage sku={sku} />
        <Card.Body>
          <LoadingComponent
            as={Card.Title}
            loading={sku === null}
            componentProps={{
              className: 'fw-bold font-size-16',
            }}
          >
            <h3 className="text-dark text-decoration-none tw-text-[16px] tw-font-bold">
              {sku === null ? 'Loading an awesome product...' : sku.name}
            </h3>
          </LoadingComponent>
          <LoadingComponent
            as={Card.Text}
            loading={sku === null}
            componentProps={{
              className: 'text-muted font-size-14 limit-2-lines',
            }}
          >
            <SkuDescription sku={sku} />
          </LoadingComponent>
        </Card.Body>
        <Card.Footer className="bg-white border-0">
          <Row className="align-items-end mb-4">
            <Col>
              <span className="font-size-14 text-muted d-block">
                {translationFallback('lowest_ask', tCommon('lowest_ask'), 'Lowest ask')}{' '}
              </span>
              <SkuCardLowestAsk sku={sku} />
            </Col>
            <Col className="col-auto">
              <SkuCardTrendingPercent sku={sku} trendingPercent={trendingPercent ?? 0} />
            </Col>
          </Row>
        </Card.Footer>
      </Card>
    </div>
  );
}
